import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import NavBar from '../components/layout/NavBar'
import { listDisputes } from '../api/adminDisputes'
import { useMe } from '../hooks/useMe'
import { getToken } from '../lib/auth'
import { formatPixeAtomic } from '../lib/formatPixe'

type Tab = 'open' | 'resolved' | 'all'

function who(user?: { handle?: string; display_name?: string } | null) {
  if (!user) return '—'
  return user.display_name || user.handle || 'User'
}

export default function AdminDisputes() {
  const authed = Boolean(getToken())
  const { me, loading: meLoading } = useMe(authed)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [count, setCount] = useState(0)
  const [rows, setRows] = useState<any[]>([])

  const status = (searchParams.get('status') || 'open').toLowerCase() as Tab
  const q = searchParams.get('q') || ''
  const page = Math.max(Number(searchParams.get('page') || '1'), 1)
  const limit = 50
  const offset = (page - 1) * limit

  useEffect(() => {
    if (!authed) return navigate('/login')
  }, [authed, navigate])

  useEffect(() => {
    if (meLoading) return
    if (!me?.is_staff) return
    let cancelled = false
    setLoading(true)
    setError(null)
    listDisputes({ status, q: q || undefined, limit, offset })
      .then((data) => {
        if (cancelled) return
        setCount(Number(data.count || 0))
        setRows(Array.isArray(data.results) ? data.results : [])
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load disputes')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [meLoading, me?.is_staff, status, q, offset])

  if (!authed) return null
  if (meLoading) return <div className="p-6 text-white/70">Loading...</div>
  if (!me?.is_staff) return <div className="p-6 text-white/70">Not found</div>

  const maxPage = Math.max(Math.ceil(count / limit), 1)

  function setTab(next: Tab) {
    const params = new URLSearchParams(searchParams)
    params.set('status', next)
    params.set('page', '1')
    setSearchParams(params)
  }

  function submitSearch(nextQ: string) {
    const params = new URLSearchParams(searchParams)
    if (nextQ.trim()) params.set('q', nextQ.trim())
    else params.delete('q')
    params.set('page', '1')
    setSearchParams(params)
  }

  function goPage(nextPage: number) {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(nextPage))
    setSearchParams(params)
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold">Admin Disputes</h1>
            <div className="text-sm text-white/65">Staff-only escrow dispute queue.</div>
          </div>
          <Link to="/settings" className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Settings</Link>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex flex-wrap items-center gap-2">
          {(['open', 'resolved', 'all'] as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setTab(tab)}
              className={`rounded-full px-3 py-1 text-xs border ${status === tab ? 'bg-brand border-transparent' : 'border-white/15 bg-white/5'}`}
            >
              {tab === 'open' ? 'Open' : tab === 'resolved' ? 'Resolved' : 'All'}
            </button>
          ))}
          <form
            className="ml-auto flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              const form = e.currentTarget as HTMLFormElement
              const input = form.elements.namedItem('q') as HTMLInputElement | null
              submitSearch(input?.value || '')
            }}
          >
            <input
              name="q"
              defaultValue={q}
              placeholder="Search job, title, users"
              className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm w-64"
            />
            <button type="submit" className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Search</button>
          </form>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 overflow-x-auto">
          {loading ? <div className="p-4 text-sm text-white/70">Loading...</div> : null}
          {error ? <div className="p-4 text-sm text-red-300">{error}</div> : null}
          {!loading && !error ? (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-white/60 border-b border-white/10">
                  <th className="px-3 py-2">Opened</th>
                  <th className="px-3 py-2">Job</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Pro</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.job_id}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                    onClick={() => navigate(`/admin/disputes/${row.job_id}`)}
                  >
                    <td className="px-3 py-2">{row.dispute_opened_at ? new Date(row.dispute_opened_at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 font-semibold">{row.title || `Job #${row.job_id}`}</td>
                    <td className="px-3 py-2">{who(row.client)}</td>
                    <td className="px-3 py-2">{who(row.pro)}</td>
                    <td className="px-3 py-2">{formatPixeAtomic(row.escrow_amount_pixe)} PIXE</td>
                    <td className="px-3 py-2">{row.job_mode}</td>
                    <td className="px-3 py-2">{row.status}</td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-white/60">No disputes found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : null}
        </div>

        <div className="flex items-center justify-between text-sm text-white/70">
          <div>{count} total</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="rounded border border-white/15 bg-white/5 px-2 py-1 disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page} / {maxPage}</span>
            <button
              type="button"
              onClick={() => goPage(Math.min(maxPage, page + 1))}
              disabled={page >= maxPage}
              className="rounded border border-white/15 bg-white/5 px-2 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
